import {DrawerBody, DrawerFooter, DrawerHeader} from '@cypro/ui/components'
import {LinkButton} from '@cypro/ui/components/ui/link-button'
import {Badge, Box, Drawer, Clipboard} from '@chakra-ui/react'
import {useTranslate} from '@tolgee/react'
import {LogDrawerChangeDetails} from './log-drawer-change-details'
import {LogDrawerHeader} from './log-drawer-header'
import {LogDrawerTimeline} from './log-drawer-timeline'
import {LuCheck, LuCopy} from 'react-icons/lu'
import {Tooltip} from '@cypro/ui/components/ui/tooltip'
import {useEventLogContext} from '@/shared/contexts/event-log'

export function LogDrawer() {
  const {t} = useTranslate('common')
  const {log} = useEventLogContext()

  return (
    <>
      <DrawerHeader>
        <Drawer.Title>
          <Box display="flex" alignItems="center" gap="10px">
            <span>{t('log_id')}:</span>
            <Clipboard.Root value={log.id} timeout={2000}>
              <Tooltip content={t('click_to_copy_id')}>
                <Clipboard.Trigger asChild>
                  <Badge
                    variant="outline"
                    p={2}
                    fontSize="xs"
                    cursor="pointer"
                    _hover={{bg: 'gray.subtle'}}
                    display="flex"
                    alignItems="center"
                    gap="2"
                  >
                    <Clipboard.Indicator copied={<LuCheck />}>
                      <LuCopy />
                    </Clipboard.Indicator>
                    {log.id}
                  </Badge>
                </Clipboard.Trigger>
              </Tooltip>
            </Clipboard.Root>
          </Box>
        </Drawer.Title>
      </DrawerHeader>

      <DrawerBody height="100%" flex="1 1 0%" spaceY="5">
        <LogDrawerHeader />
        <LogDrawerTimeline />
        <LogDrawerChangeDetails />
      </DrawerBody>

      <DrawerFooter>
        {Boolean(log) && (
          <ResourceButton
            resource={log.resource}
            resourceId={log.resourceId}
            patientId={log.details?.patientId}
            label={t('view_resource')}
          />
        )}
      </DrawerFooter>
    </>
  )
}

const RESOURCE_ROUTES = {
  pharmacy: {root: '/configuration/pharmacies/new', idParam: 'pharmacyId'},
  user: {root: '/configuration/users/new', idParam: 'userId'},
  customer: {root: '/configuration/customers/new', idParam: 'customerId'},
  'active-substance-details': {root: '/configuration/drug-catalog/active-substances/new', idParam: 'substanceId'},
  'active-substance': {root: '/configuration/drug-catalog/active-substances/new', idParam: 'substanceId'},
  'drug-catalog-item': {root: '/configuration/drug-catalog/active-substances/new', idParam: 'substanceId'},
  section: {root: '/configuration/sections/new', idParam: 'sectionId'},
  ward: {root: '/configuration/wards/new', idParam: 'wardId'},
} as const

type ResourceButtonProps = {
  resource: string | null
  resourceId: string | null
  patientId?: string | null
  label: string
}

const isValidResource = (resource: string): resource is keyof typeof RESOURCE_ROUTES => {
  return resource in RESOURCE_ROUTES
}

const isPatientResource = (resource: string): boolean => {
  return resource.startsWith('patient')
}

function ResourceButton({resource, resourceId, patientId, label}: ResourceButtonProps) {
  if (!resource || !resourceId) return null

  if (isPatientResource(resource)) {
    return (
      <LinkButton
        to={'/patient-dashboard/patients/$patientId'}
        params={{patientId: patientId ?? resourceId}}
        colorPalette="cyan"
      >
        {label}
      </LinkButton>
    )
  }

  if (!isValidResource(resource)) return null

  return (
    <LinkButton
      to={RESOURCE_ROUTES[resource].root}
      search={{[RESOURCE_ROUTES[resource].idParam]: resourceId}}
      colorPalette="cyan"
    >
      {label}
    </LinkButton>
  )
}
